import * as z from "zod";
import {
  Session,
  useGetAvailableDays,
  useRescheduleSession,
  useScheduleSession,
} from "@/lib/api/sessions.ts";
import { useToast } from "@/components/ui/use-toast.ts";
import { useNoteUtils } from "@/lib/hooks/conversations/use-note-utils.ts";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { cn } from "@/lib/utils.ts";
import { Label } from "@/components/ui/label.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Check, Loader2 } from "lucide-react";
import { NoteReminderForm } from "@/components/conversation-details/notes/note-reminder-form.tsx";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";

const sessionSchema = z.object({
  note: z.string().optional(),
  date: z.date(),
  time: z.any(),
  timezone: z.string().optional(),
});

type SessionSchema = z.infer<typeof sessionSchema>;

interface ScheduleSessionProps {
  closeForm: () => void;
  menteeId: string;
  session?: Session; // optional session to edit
  handleSessionUpdated?: () => void;
}

export function SessionForm({
  closeForm,
  menteeId,
  session,
  handleSessionUpdated,
}: ScheduleSessionProps) {
  const { toast } = useToast();
  const { data: disabledDates } = useGetAvailableDays();
  const { track } = useTrackEvent();

  const { convertFormValuesToReminder, convertReminderToFormValues } =
    useNoteUtils();
  const { mutateAsync: scheduleSession, isPending: isScheduling } =
    useScheduleSession(menteeId);
  const { mutateAsync: rescheduleSession, isPending: isRescheduling } =
    useRescheduleSession(menteeId);

  const initialData = convertReminderToFormValues(
    session?.scheduled_time,
    session?.metadata?.timezone,
  );

  const form = useForm<SessionSchema>({
    resolver: zodResolver(sessionSchema),
    defaultValues: {
      note: session?.note || "",
      date: initialData?.date,
      time: initialData?.time,
      timezone: initialData?.timezone,
    },
  });

  const handleSubmit = form.handleSubmit(async (data) => {
    try {
      const payload = {
        note: data.note,
        scheduled_time: convertFormValuesToReminder(
          data.date,
          data.time,
          data.timezone,
        ) as string,
        timezone: data.timezone,
        mentee_id: menteeId,
      };

      if (session) {
        await rescheduleSession({ ...payload, session_id: session.id });
        toast({
          title: "Session Rescheduled",
          description: "The session has been rescheduled successfully",
        });
      } else {
        await scheduleSession(payload);
        track(PostHogEvents.SESSION_SCHEDULED, {
          teen_id: menteeId,
          scheduled_time: payload.scheduled_time,
        });
        toast({
          title: "Session Scheduled",
          description: "The session has been scheduled successfully",
        });
      }

      handleSessionUpdated?.();

      closeForm();
    } catch (error) {
      toast({
        title: "Session Scheduling Failed",
        description:
          "Something went wrong while scheduling the session, please try again",
        variant: "destructive",
      });
    }
  });

  // the max date is end of the current month
  const maxDate = new Date();
  maxDate.setDate(1);
  maxDate.setMonth(maxDate.getMonth() + 1);
  maxDate.setDate(0);

  return (
    <div className={cn("mt-2 flex flex-row items-end justify-between gap-2")}>
      <div className="w-full">
        <Label className="text-sm font-normal">Note</Label>
        <Input
          placeholder="Enter a note"
          className="mt-2 rounded-md"
          value={form.watch("note")}
          onChange={(e) => form.setValue("note", e.target.value)}
        />

        <NoteReminderForm
          setValue={(key, value) => form.setValue(key as any, value)}
          date={form.watch("date")}
          time={form.watch("time")}
          timezone={form.watch("timezone")}
          disabledDates={disabledDates}
          showPresets={false}
          maxDate={maxDate}
        />
      </div>
      <button
        className={cn(
          "mb-2 flex items-center justify-center transition-opacity active:opacity-50",
        )}
        onClick={handleSubmit}
        disabled={isScheduling || isRescheduling}
      >
        {isScheduling || isRescheduling ? (
          <Loader2 className="h-6 w-6 animate-spin" />
        ) : (
          <Check className="text-gray-400" size={20} />
        )}
      </button>
    </div>
  );
}
